import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Heart, Star, Users } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { AuthPhotoPanel } from './PromoPhotos';
import { supportedCities } from '../data/supportedCities';
import PhotoCapture from './PhotoCapture';
import GoogleSignInButton from './GoogleSignInButton';
import { ForgotPasswordForm } from './AccountRecovery';

const AuthScreen = () => {
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    birthday: '',
    gender: '',
    looking_for: [],
    location: supportedCities[0].name, // Default to first city
    bio: ''
  });
  const [showForgot, setShowForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(1); // 1: Basic Info, 2: Photo Capture
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  
  const { login, register } = useAuth();
  const { toast } = useToast();
  useEffect(() => {
    const notice = sessionStorage.getItem('auth_notice');
    if (notice) {
      sessionStorage.removeItem('auth_notice');
      toast({ title: "Signed out", description: notice, variant: "destructive" });
    }
  }, []); // eslint-disable-line

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in."
      });
    } else {
      toast({
        title: "Login Failed",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (registerData.looking_for.length === 0) {
      toast({
        title: "Selection Required",
        description: "Please select at least one gender preference.",
        variant: "destructive"
      });
      return;
    }
    
    const dob = new Date(registerData.birthday);
    const ageMs = Date.now() - dob.getTime();
    const age = Math.floor(ageMs / (365.25 * 24 * 3600 * 1000));
    if (!registerData.birthday || Number.isNaN(dob.getTime()) || age < 18) {
      toast({ title: "18+ only", description: "You must be at least 18 years old to join FlameLink.", variant: "destructive" });
      return;
    }
    if (registerData.password.length < 8 || !/[A-Za-z]/.test(registerData.password) || !/\d/.test(registerData.password)) {
      toast({ title: "Weak password", description: "Use at least 8 characters with letters and numbers.", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    
    try {
      // Convert birthday string to Date object
      const registrationData = {
        ...registerData,
        birthday: new Date(registerData.birthday).toISOString()
      };
      
      const result = await register(registrationData);
      
      if (result.success) {
        // Move to photo capture step
        setRegistrationStep(2);
        setShowPhotoCapture(true);
        toast({
          title: "Account Created!",
          description: "Now let's add your profile photo."
        });
      } else {
        toast({
          title: "Registration Failed",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Registration Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  const handlePhotoSubmit = (photoData) => {
    // TODO: Upload photo to user profile
    console.log('Photo submitted:', photoData);
    
    toast({
      title: "Profile Complete!",
      description: "Welcome to FlameLink! Start swiping to find your matches."
    });
    
    // Photo submitted successfully, user should now be logged in and redirect to swiping
    // The register function already handles login, so we just need to close the photo capture
    setShowPhotoCapture(false);
  };

  const handleSkipPhoto = () => {
    toast({
      title: "Profile Created!",
      description: "You can add photos later in your profile settings."
    });
    
    setShowPhotoCapture(false);
  };

  return (
    <>
      {showPhotoCapture ? (
        <PhotoCapture
          onPhotoSubmit={handlePhotoSubmit}
          onSkip={handleSkipPhoto}
        />
      ) : (
        <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#fde2e4_0%,_#fbcfe8_35%,_#fdf2f8_70%,_#fff7ed_100%)] flex items-center justify-center p-4">
          <div className="w-full max-w-md lg:max-w-5xl grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,28rem)] gap-8 items-stretch min-w-0" data-testid="auth-layout">
          <AuthPhotoPanel />
          <Card className="w-full max-w-md mx-auto min-w-0 p-6 sm:p-8 lg:self-center" data-testid="auth-card">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center justify-center gap-2 mb-4" data-testid="auth-brand">
            <BrandLogo variant="script" className="h-28 sm:h-32 w-auto drop-shadow-md" sizes="160px" priority />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-500 via-rose-500 to-fuchsia-600 bg-clip-text text-transparent">
              FlameLink
            </h1>
          </div>
          <p className="text-gray-600">Find meaningful connections in 50 major global cities</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            {showForgot ? <ForgotPasswordForm onBack={() => setShowForgot(false)} /> : (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  placeholder="Enter your email"
                  required
                  data-testid="login-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-red-500 mt-2 hover:underline" data-testid="forgot-password-link">Forgot password?</button>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
              <div className="flex items-center gap-3 pt-1" aria-hidden="true">
                <div className="h-px flex-1 bg-gray-200" /><span className="text-xs text-gray-400">or</span><div className="h-px flex-1 bg-gray-200" />
              </div>
              <GoogleSignInButton />
            </form>
            )}
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <Input
                  data-testid="register-name-input"
                  type="text"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  data-testid="register-email-input"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Create a password (8+ chars, letters & numbers)"
                  minLength={8}
                  data-testid="register-password-input"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birthday
                </label>
                <Input
                  data-testid="register-birthday-input"
                  type="date"
                  value={registerData.birthday}
                  onChange={(e) => setRegisterData({ ...registerData, birthday: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  I am
                </label>
                <select
                  data-testid="register-gender-select"
                  value={registerData.gender}
                  onChange={(e) => setRegisterData({ ...registerData, gender: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="">Select your gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Looking for
                </label>
                <div className="space-y-2">
                  {['male', 'female', 'non-binary', 'other'].map((gender) => (
                    <label key={gender} className="flex items-center">
                      <input
                        type="checkbox"
                        data-testid={`register-looking-for-${gender}`}
                        checked={registerData.looking_for.includes(gender)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRegisterData({
                              ...registerData,
                              looking_for: [...registerData.looking_for, gender]
                            });
                          } else {
                            setRegisterData({
                              ...registerData,
                              looking_for: registerData.looking_for.filter(g => g !== gender)
                            });
                          }
                        }}
                        className="mr-2 h-4 w-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{gender}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  data-testid="register-location-select"
                  value={registerData.location}
                  onChange={(e) => setRegisterData({ ...registerData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                >
                  {supportedCities.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio (Optional)
                </label>
                <Input
                  data-testid="register-bio-input"
                  type="text"
                  value={registerData.bio}
                  onChange={(e) => setRegisterData({ ...registerData, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                />
              </div>
              
              <Button 
                type="submit" 
                  data-testid="register-submit-btn"
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </TabsContent>

        </Tabs>

        {/* Features Preview */}
        <div className="mt-8 pt-6 border-t">
          <h3 className="text-center text-sm font-medium text-gray-700 mb-4">
            Enhanced Dating Features
          </h3>
          <div className="flex justify-center space-x-6 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <Heart className="w-4 h-4 text-red-400" />
              <span>Smart Matching</span>
            </div>
            <div className="flex items-center space-x-1">
              <Star className="w-4 h-4 text-blue-400" />
              <span>Compatibility Scoring</span>
            </div>
            <div className="flex items-center space-x-1">
              <Users className="w-4 h-4 text-green-400" />
              <span>Verified Profiles</span>
            </div>
          </div>
        </div>
      </Card>
          </div>
    </div>
      )}
    </>
  );
};

export default AuthScreen;